import { clamp, cos, sin, smoothstep } from './dmath.ts';
import { L } from './config.ts';

export interface LaneRoute { points: Float64Array; length: number; segments: number }
type V = [number, number, number];

/** One coordinate of a centripetal Catmull-Rom segment (the same construction as three.js's CatmullRomCurve3). */
function nonuniform(x0: number, x1: number, x2: number, x3: number, dt0: number, dt1: number, dt2: number, t: number) {
  let t1 = (x1 - x0) / dt0 - (x2 - x0) / (dt0 + dt1) + (x2 - x1) / dt1;
  let t2 = (x2 - x1) / dt1 - (x3 - x1) / (dt1 + dt2) + (x3 - x2) / dt2;
  t1 *= dt1; t2 *= dt1;
  const c2 = -3 * x1 + 3 * x2 - 2 * t1 - t2, c3 = 2 * x1 - 2 * x2 + t1 + t2;
  return x1 + t1 * t + c2 * t * t + c3 * t * t * t;
}
const d2 = (a: V, b: V) => (a[0] - b[0]) * (a[0] - b[0]) + (a[1] - b[1]) * (a[1] - b[1]) + (a[2] - b[2]) * (a[2] - b[2]);

function catmull(points: V[], t: number): V {
  const l = points.length, p = (l - 1) * t;
  let i = Math.floor(p), w = p - i;
  if (w === 0 && i === l - 1) { i = l - 2; w = 1; }
  const p1 = points[i], p2 = points[i + 1];
  const p0: V = i > 0 ? points[i - 1] : [2 * p1[0] - p2[0], 2 * p1[1] - p2[1], 2 * p1[2] - p2[2]];
  const p3: V = i + 2 < l ? points[i + 2] : [2 * p2[0] - p1[0], 2 * p2[1] - p1[1], 2 * p2[2] - p1[2]];
  // centripetal: knot spacing = distance^0.5 = (distance²)^0.25
  let dt0 = Math.sqrt(Math.sqrt(d2(p0, p1))), dt1 = Math.sqrt(Math.sqrt(d2(p1, p2))), dt2 = Math.sqrt(Math.sqrt(d2(p2, p3)));
  if (dt1 < 1e-4) dt1 = 1; if (dt0 < 1e-4) dt0 = dt1; if (dt2 < 1e-4) dt2 = dt1;
  return [nonuniform(p0[0], p1[0], p2[0], p3[0], dt0, dt1, dt2, w), nonuniform(p0[1], p1[1], p2[1], p3[1], dt0, dt1, dt2, w), nonuniform(p0[2], p1[2], p2[2], p3[2], dt0, dt1, dt2, w)];
}

/** Arc-length table and evenly spaced samples, as CatmullRomCurve3.getSpacedPoints does. */
function spaced(points: V[], segments: number, divisions = 512) {
  const lengths = [0];
  let last = catmull(points, 0), sum = 0;
  for (let p = 1; p <= divisions; p++) {
    const cur = catmull(points, p / divisions);
    sum += Math.sqrt(d2(cur, last)); lengths.push(sum); last = cur;
  }
  const out = new Float64Array((segments + 1) * 3);
  for (let d = 0; d <= segments; d++) {
    const target = (d / segments) * sum;
    let low = 0, high = divisions, i = 0;
    while (low <= high) {
      i = Math.floor(low + (high - low) / 2);
      const cmp = lengths[i] - target;
      if (cmp < 0) low = i + 1; else if (cmp > 0) high = i - 1; else { high = i; break; }
    }
    i = high;
    const t = lengths[i] === target ? i / divisions : (i + (target - lengths[i]) / (lengths[i + 1] - lengths[i])) / divisions;
    const q = catmull(points, t);
    out[d * 3] = q[0]; out[d * 3 + 1] = q[1]; out[d * 3 + 2] = q[2];
  }
  return { points: out, length: sum };
}

/** Smooth, distance-sampled routes built once. Per-enemy sampling allocates nothing. */
export function buildEnemyRoutes(): LaneRoute[] {
  return L.lanes.map((z, lane) => {
    const across = (lane - 1.5) * 0.16;
    const c = cos(L.gateAngle), s = sin(L.gateAngle);
    const gate: V = [L.gateX + c * across, 0, L.gateZ - s * across];
    const front: V = [s, 0, c];
    const segments = 192;
    const { points, length } = spaced([
      [L.spawnX - lane * 0.13, 0, z],
      [L.fogEndX + 0.8, 0, z],
      [L.gateX - 1.8, 0, z],
      [gate[0] + front[0] * 0.85, 0, gate[2] + front[2] * 0.85],
      gate,
      [gate[0] - front[0] * 1.05, 0, gate[2] - front[2] * 1.05],
    ], segments);
    return { points, length, segments };
  });
}

export interface RoutePoint { x: number; z: number; dx: number; dz: number }

/** Position and unit direction on the route at distance `distance`. */
export function sampleEnemyRoute(route: LaneRoute, distance: number, out: RoutePoint): RoutePoint {
  const f = clamp(distance / route.length, 0, 1) * route.segments;
  const i = Math.min(route.segments - 1, Math.floor(f)), u = f - i, a = i * 3, b = a + 3, p = route.points;
  out.x = p[a] + (p[b] - p[a]) * u; out.z = p[a + 2] + (p[b + 2] - p[a + 2]) * u;
  const dx = p[b] - p[a], dz = p[b + 2] - p[a + 2], len = Math.sqrt(dx * dx + dz * dz) || 1;
  out.dx = dx / len; out.dz = dz / len;
  return out;
}

/** Position on a lane at distance s, with the enemy's own lane offset fading out before the gate. */
export function laneAt(routes: LaneRoute[], lane: number, s: number, out: RoutePoint, laneZ?: number): RoutePoint {
  sampleEnemyRoute(routes[lane], s, out);
  if (laneZ !== undefined) out.z += (laneZ - L.lanes[lane]) * clamp((L.gateX - 1.8 - out.x) / 1.0, 0, 1);
  return out;
}

/** 0 → 1 as an enemy condenses out of the fog bank. */
export function emergenceAt(x: number) { return smoothstep(x, L.revealStartX, L.revealEndX); }
